/**
 * SnapshotPage - Read-only view of a saved search snapshot.
 *
 * Renders the frozen results without search bar or interactive elements.
 * Supports both search result snapshots and compare-grid snapshots.
 * Public route: no authentication required.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useSnapshotQuery } from '../hooks/useSnapshot';
import SearchResultsTable from '../components/SearchResultsTable';
import SplContentRenderer from '../components/SplContentRenderer';
import { formatSummaryHtml } from '../lib/formatSummary';
import { type ExportDrug } from '../utils/compareExport';
import HighlightLegend from '../components/HighlightLegend';
import SnapshotPdfViewer from '../components/SnapshotPdfViewer';
import { JP_CONTENT_STYLE } from '../components/viewer/JPBilingualSections';
import type { ColoredTerm } from '../utils/highlightColors';

interface CompareGridDrug {
  drug_id: number;
  brand_name?: string | null;
  generic_name?: string | null;
  manufacturer?: string | null;
}

interface CompareGridCell {
  drug_id: number;
  title?: string | null;
  content_text?: string | null;
  content_xml?: string | null;
  has_content: boolean;
}

interface CompareGridRow {
  section_number: string;
  title: string;
  cells: CompareGridCell[];
}

/**
 * Read-only compare grid renderer for snapshot view.
 */
const SnapshotCompareGrid: React.FC<{
  drugs: CompareGridDrug[];
  rows: CompareGridRow[];
  keyword?: string;
  regulatory?: boolean;
  coloredTerms?: ColoredTerm[];
}> = ({ drugs, rows, keyword, regulatory, coloredTerms }) => {
  const [expandedCell, setExpandedCell] = useState<string | null>(null);
  const numDrugs = drugs.length;

  // Multi-row queries → one color per row. Fall back to the single keyword.
  const multi = !!coloredTerms && coloredTerms.length > 1;
  const terms = multi ? coloredTerms!.map((c) => c.term) : keyword ? [keyword] : [];
  const colors = multi ? coloredTerms!.map((c) => c.color) : undefined;

  return (
    <div className="flex flex-1 min-h-0 flex-col gap-2">
      {multi && (
        <div className="rounded-lg border border-slate-100 bg-slate-50/60 px-3 py-2">
          <HighlightLegend terms={coloredTerms!} />
        </div>
      )}
    <div className="overflow-auto border border-slate-200 rounded-xl flex-1 min-h-0">
      <div
        className="inline-grid min-w-full"
        style={{
          gridTemplateColumns: `200px repeat(${numDrugs}, minmax(300px, 1fr))`,
        }}
      >
        {/* Header row */}
        <div className="sticky top-0 left-0 z-30 bg-slate-100 border-b border-r border-slate-300 p-3 font-semibold text-sm text-slate-600">
          Section
        </div>
        {drugs.map((d) => (
          <div
            key={d.drug_id}
            className="sticky top-0 z-20 bg-slate-100 border-b border-slate-300 p-3"
          >
            <div className="font-semibold text-sm text-slate-800 truncate">
              {d.brand_name || d.generic_name || `Drug ${d.drug_id}`}
            </div>
            {d.manufacturer && (
              <div className="text-xs text-slate-500 truncate">{d.manufacturer}</div>
            )}
          </div>
        ))}

        {/* Data rows */}
        {rows.map((row) => (
          <React.Fragment key={row.section_number}>
            <div className="sticky left-0 z-10 bg-white border-b border-r border-slate-200 p-3">
              <div className="font-medium text-xs text-indigo-600">{row.section_number}</div>
              <div className="text-xs text-slate-600 line-clamp-2">{row.title}</div>
            </div>
            {row.cells.map((cell) => {
              const cellKey = `${row.section_number}-${cell.drug_id}`;
              const isExpanded = expandedCell === cellKey;
              return (
                <div
                  key={cell.drug_id}
                  className={`border-b border-slate-200 p-3 ${
                    cell.has_content ? 'bg-white' : 'bg-slate-50'
                  } ${isExpanded ? '' : 'max-h-64 overflow-hidden relative'}`}
                >
                  {cell.has_content ? (
                    <>
                      {cell.title && (
                        <div className="text-xs font-medium text-slate-400 mb-1.5 truncate">{cell.title}</div>
                      )}
                      <div className={isExpanded ? '' : 'line-clamp-[10]'}>
                        <SplContentRenderer
                          contentXml={regulatory ? null : (cell.content_xml ?? null)}
                          contentText={cell.content_text ?? null}
                          drugName=""
                          {...(regulatory ? { contentHtml: cell.content_xml ?? null } : {})}
                          {...(terms.length ? { highlightTerms: terms } : {})}
                          {...(colors ? { highlightColors: colors } : {})}
                        />
                      </div>
                      {!isExpanded && (
                        <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white to-transparent flex items-end justify-center pb-1">
                          <button
                            onClick={() => setExpandedCell(cellKey)}
                            className="text-xs text-indigo-600 hover:text-indigo-800 font-medium bg-white/80 px-2 py-0.5 rounded"
                          >
                            Show more
                          </button>
                        </div>
                      )}
                      {isExpanded && (
                        <button
                          onClick={() => setExpandedCell(null)}
                          className="mt-2 text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                        >
                          Show less
                        </button>
                      )}
                    </>
                  ) : (
                    <span className="text-xs text-slate-400 italic">No content</span>
                  )}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
    </div>
  );
};

/** Keyword-compare snapshot: one column per drug — PDF drugs render the real
 *  zoomable page viewer; section-text drugs render their highlighted blocks. */
/** One snapshot text column. On first render it scrolls itself to the evidence
 *  chunk (.evidence-chunk-highlight) so the relevant part is visible at a glance;
 *  afterwards scrolling is the viewer's. */
const SnapshotTextColumn: React.FC<{ d: ExportDrug }> = ({ d }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  // JP columns carry both languages (html_ja). Offer an EN/日本語 toggle, English
  // by default — same as the live JP Compare panel.
  const bilingual = d.blocks.some((b) => b.html_ja);
  const [lang, setLang] = useState<'en' | 'ja'>('en');
  // Scroll to the match exactly ONCE: the Cogni evidence block, else the first
  // keyword <mark> — same as the live Compare column. Both live in the ENGLISH
  // content, so only run while EN is shown; the guard keeps a later EN⇄JA toggle
  // from yanking the reader back to the match.
  const didScroll = useRef(false);
  useEffect(() => {
    if (didScroll.current || lang !== 'en') return;
    const c = scrollRef.current;
    if (!c) return;
    let tries = 0;
    const iv = setInterval(() => {
      const el = (c.querySelector('.evidence-chunk-highlight') ?? c.querySelector('mark')) as HTMLElement | null;
      if (el) {
        c.scrollTop += el.getBoundingClientRect().top - c.getBoundingClientRect().top - 16;
        didScroll.current = true;
        clearInterval(iv);
      } else if (++tries > 20) {
        clearInterval(iv);
      }
    }, 50);
    return () => clearInterval(iv);
  }, [lang]);
  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="flex items-start justify-between gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-slate-800">{d.name}</div>
          {d.subtitle && <div className="truncate text-xs text-slate-500">{d.subtitle}</div>}
        </div>
        {bilingual && (
          <div className="inline-flex shrink-0 rounded-full border border-slate-200 bg-white p-0.5 text-[11px]">
            {(['en', 'ja'] as const).map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => setLang(l)}
                className={
                  'rounded-full px-2 py-0.5 transition-colors ' +
                  (lang === l ? 'bg-indigo-600 text-white' : 'text-slate-500 hover:text-slate-700')
                }
                aria-pressed={lang === l}
              >
                {l === 'en' ? 'EN' : '日本語'}
              </button>
            ))}
          </div>
        )}
      </header>
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 text-sm leading-relaxed text-slate-700">
        {d.note && (
          <div className="mb-2 rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">{d.note}</div>
        )}
        {d.blocks.map((b, j) => {
          // English by default; Japanese when toggled AND that block has a JA
          // counterpart (fall back to English so nothing ever blanks out).
          const useJa = lang === 'ja' && !!b.html_ja;
          const heading = useJa ? (b.heading_ja || b.heading) : b.heading;
          const html = useJa ? b.html_ja : b.html;
          return (
            <div key={j} className="mb-4 border-t border-slate-100 pt-3 first:border-t-0 first:pt-0">
              {heading && <h4 className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-900">{heading}</h4>}
              {html ? (
                <div className="[&_mark]:rounded [&_mark]:px-0.5 jp-content" dangerouslySetInnerHTML={{ __html: html }} />
              ) : (
                <div className="whitespace-pre-wrap">{b.text}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const SnapshotKeywordCompare: React.FC<{ drugs: ExportDrug[]; coloredTerms?: ColoredTerm[] | undefined }> = ({
  drugs,
  coloredTerms,
}) => {
  // Always the side-by-side scrollable column layout — each drug is an
  // independently-scrollable column (full label sections OR PDF pages), so the
  // snapshot is self-sufficient and readable for keyword AND Cogni compares
  // (Cogni columns are all-SPL, which previously fell back to a cramped table).
  return (
    <div className="flex flex-col gap-2">
      {/* JP bilingual blocks render real PMDA tables — include the shared JP
          content CSS so they're styled in the snapshot (it normally lives in a
          <style> inside JPBilingualSections, which isn't mounted here). */}
      <style>{JP_CONTENT_STYLE}</style>
      {coloredTerms && coloredTerms.length > 1 && (
        <div className="rounded-lg border border-slate-100 bg-slate-50/60 px-3 py-2">
          <HighlightLegend terms={coloredTerms} />
        </div>
      )}
      <div className="flex h-[80vh] gap-3 overflow-x-auto">
        {drugs.map((d, i) => (
          // FIXED-width, non-shrinking columns (like the live N-column compare's
          // COL_W) so many drugs scroll HORIZONTALLY instead of squeezing each
          // column down to an unreadable width. The strip itself scrolls (parent
          // overflow-x-auto).
          <div key={i} className="flex w-[440px] shrink-0 flex-col overflow-hidden rounded-lg border border-slate-200">
            {d.pdf ? (
              <SnapshotPdfViewer name={d.name} subtitle={d.subtitle} pdf={d.pdf} />
            ) : (
              <SnapshotTextColumn d={d} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const SnapshotPage: React.FC = () => {
  const { shareToken } = useParams<{ shareToken: string }>();
  const navigate = useNavigate();
  const { data: snapshot, isLoading, error } = useSnapshotQuery(shareToken);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <svg className="w-8 h-8 animate-spin text-indigo-500 mx-auto mb-3" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <p className="text-slate-500">Loading snapshot...</p>
        </div>
      </div>
    );
  }

  if (error || !snapshot) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-4xl mb-4">404</div>
          <h1 className="text-xl font-semibold text-slate-800 mb-2">Snapshot not found</h1>
          <p className="text-slate-500 mb-6">This snapshot may have been deleted or the link is invalid.</p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Go to Precepi
          </Link>
        </div>
      </div>
    );
  }

  const createdDate = new Date(snapshot.created_at).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  // Detect snapshot type
  const meta = snapshot.metadata as Record<string, unknown> | null;
  const isCompareGrid = meta?.['type'] === 'compare_grid';
  const isQaConversation = meta?.['type'] === 'qa_conversation';
  const isKeywordCompare = meta?.['type'] === 'keyword_compare';
  const qaMessages = isQaConversation ? (meta?.['messages'] as { role: string; content: string }[] ?? []) : [];
  const qaDrugNames = isQaConversation ? (meta?.['drug_names'] as string[] ?? []) : [];

  // Approval dates are reliable for every region (2026-10-15 backfill); the
  // column is always shown here.
  const showApprovalDate = true;

  return (
    <div className={`min-h-screen bg-slate-50 ${isCompareGrid ? 'h-screen flex flex-col' : ''}`}>
      {/* Banner */}
      <div className="bg-indigo-600 text-white py-3 px-4 shrink-0">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="text-indigo-200 hover:text-white transition-colors"
              title="Go back"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <svg className="w-5 h-5 opacity-80" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
            </svg>
            <span className="text-sm font-medium">
              Saved snapshot from {createdDate}
            </span>
          </div>
          <Link
            to="/"
            className="text-sm text-indigo-100 hover:text-white transition-colors underline underline-offset-2"
          >
            Open Precepi
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className={isCompareGrid || isKeywordCompare ? 'px-4 py-6 flex-1 min-h-0 flex flex-col' : 'max-w-7xl mx-auto px-4 py-6'}>
        {/* Query header */}
        <div className="mb-6 shrink-0">
          <h1 className="text-2xl font-bold text-slate-800 mb-1">
            "{snapshot.query}"
          </h1>
          <p className="text-sm text-slate-500">
            {isCompareGrid
              ? `${snapshot.result_count ?? snapshot.drug_results.length} drugs compared`
              : isQaConversation
              ? `${qaMessages.filter(m => m.role === 'user').length} question${qaMessages.filter(m => m.role === 'user').length !== 1 ? 's' : ''} · ${qaDrugNames.join(', ')}`
              : (
                <>
                  {snapshot.result_count ?? snapshot.drug_results.length} results
                  {snapshot.filters && !!(snapshot.filters as Record<string, unknown>)['region'] && (snapshot.filters as Record<string, unknown>)['region'] !== 'ALL' && (
                    <span> · {(snapshot.filters as Record<string, unknown>)['region'] as string} only</span>
                  )}
                </>
              )}
          </p>
        </div>

        {/* Summary (skip for QA conversations — messages already show the content) */}
        {snapshot.summary && !isQaConversation && (
          <div className="mb-6 p-5 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="text-sm font-semibold text-blue-900 mb-2">AI Summary</h3>
            <div
              className="text-sm text-gray-700 leading-relaxed [&_strong]:font-semibold [&_strong]:text-blue-800 [&_em]:italic [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2 [&_li]:mb-1 [&_p]:my-2"
              dangerouslySetInnerHTML={{ __html: formatSummaryHtml(snapshot.summary) }}
            />
          </div>
        )}

        {/* Render based on snapshot type */}
        {isKeywordCompare ? (
          // Full comparison content lives in metadata.drugs (not drug_results,
          // which is 2 MB-capped). PDF drugs carry their pages → zoomable viewer.
          <SnapshotKeywordCompare
            drugs={((meta?.['drugs'] as unknown[]) ?? snapshot.drug_results) as unknown as ExportDrug[]}
            coloredTerms={meta?.['coloredTerms'] as ColoredTerm[] | undefined}
          />
        ) : isCompareGrid ? (
          <SnapshotCompareGrid
            drugs={snapshot.drug_results as unknown as CompareGridDrug[]}
            rows={(meta?.['rows'] ?? []) as CompareGridRow[]}
            {...(meta?.['keyword'] ? { keyword: meta['keyword'] as string } : {})}
            {...(meta?.['regulatory'] ? { regulatory: true } : {})}
            {...(meta?.['coloredTerms'] ? { coloredTerms: meta['coloredTerms'] as ColoredTerm[] } : {})}
          />
        ) : isQaConversation ? (
          <div className="space-y-4">
            {qaDrugNames.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap mb-2">
                {qaDrugNames.map((name) => (
                  <span key={name} className="px-2.5 py-1 bg-indigo-50 border border-indigo-200/60 rounded-full text-xs font-medium text-indigo-800">
                    {name}
                  </span>
                ))}
              </div>
            )}
            {qaMessages.map((msg, i) => (
              <div key={i} className={`rounded-lg p-4 ${msg.role === 'user' ? 'bg-white border border-slate-200' : 'bg-blue-50 border border-blue-200'}`}>
                <div className={`text-xs font-semibold mb-2 ${msg.role === 'user' ? 'text-slate-500' : 'text-blue-700'}`}>
                  {msg.role === 'user' ? 'Question' : 'Answer'}
                </div>
                {msg.role === 'assistant' ? (
                  <div
                    className="text-sm text-gray-700 leading-relaxed [&_strong]:font-semibold [&_strong]:text-blue-800 [&_em]:italic [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2 [&_li]:mb-1 [&_p]:my-2"
                    dangerouslySetInnerHTML={{ __html: formatSummaryHtml(msg.content) }}
                  />
                ) : (
                  <p className="text-sm text-slate-700">{msg.content}</p>
                )}
              </div>
            ))}
          </div>
        ) : snapshot.drug_results.length > 0 ? (
          <SearchResultsTable
            results={snapshot.drug_results}
            onDrugClick={() => {}}
            selectedDrugIds={[]}
            onDrugSelect={() => {}}
            maxCompare={0}
            showRelevanceScore={false}
            showApprovalDate={showApprovalDate}
          />
        ) : (
          <div className="text-center py-16 text-slate-500">
            No results in this snapshot.
          </div>
        )}
      </div>
    </div>
  );
};

export default SnapshotPage;
